import React, {useEffect, useState} from 'react';
import {Button, Flex, PriorityIcon} from '@priority-software/priority-style-react';
import styles from '../styles/formDesignContent.module.scss';
import {List} from './List';
import {ItemType} from '../types';
import {ActionsList} from './ActionsList';
import {DesignPanels, MoveDirection} from '../enums';
import {FormViewEnum} from '../../../stores/Form/Form';
import {TabViewList} from './TabViewList';
import {SimpleItem} from './Item';
import {SearchInput} from './SearchInput';
import {Buttons} from './Buttons';
import {reorderSimpleList} from '../utils/reorder';
import {getFieldMenuData} from '../utils/submenu';
import {priority} from '@priority-software/priority-proto-api/lib';
import text from '../texts';
import {getCurrentForm, isFieldMandatory} from '../utils/crossAPI';
import Loader from './Loader';
import {deserializeTabData, serializeTabData} from '../utils/tab';

type FormDesignContentProps = {
    data: ItemType[];
    pic: priority.netitems.form.Picture;
    setPic: (pic: priority.netitems.form.Picture) => void;
    activePanel: DesignPanels;
    updateData: (data: ItemType[]) => void;
    isLoading: boolean;
    packages: priority.netitems.ngen.IPackage[];
    setPackages: (packages: priority.netitems.ngen.IPackage[]) => void;
};

export const FieldsTableDesignContent = ({
    activePanel,
    data,
    updateData,
    pic,
    setPic,
    isLoading,
    packages,
    setPackages
}: FormDesignContentProps) => {
    const [foundItemName, setFoundItemName] = useState(null);
    const [viewItemName, setViewItemName] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);

    const resetSelected = () => {
        setSelectedItems([]);
    };

    useEffect(() => {
        if (foundItemName != null) {
            setTimeout(() => {
                setFoundItemName(null);
            }, 6000);
        }
    }, [foundItemName]);

    useEffect(() => {
        if (viewItemName.length) {
            setTimeout(() => {
                setViewItemName([]);
            }, 2000);
        }
    }, [viewItemName]);

    const moveItems = (dir: MoveDirection) => {
        const result = reorderSimpleList(dir, data, selectedItems);
        setViewItem(selectedItems);
        updateData(result);
    };

    const setViewItem = (selectedArr: ItemType[]) => {
        setViewItemName(selectedArr.map(i => i?.Name));
    };

    const hideSelected = () => {
        selectedItems.forEach(i => {
            if (!isFieldMandatory(i.Name)) {
                i.Hidden = priority.netitems.form.Hide.HiddenByDesign;
            }
            if (i.merged && !isFieldMandatory(i.merged.Name)) {
                i.merged.Hidden = priority.netitems.form.Hide.HiddenByDesign;
            }
        });
        resetSelected();
        onUpdate();
    };

    const onUpdate = () => {
        updateData(data);
    };

    return (
        <Flex direction="column" componentClasses={styles.container}>
            {activePanel === DesignPanels.FIELDS && getCurrentForm().view === FormViewEnum.form && (
                <TabViewContent
                    data={data}
                    pic={pic}
                    setPic={setPic}
                    updateData={updateData}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    resetSelected={resetSelected}
                    foundItemName={foundItemName}
                    setFoundItemName={setFoundItemName}
                    setViewItem={setViewItem}
                    viewItemName={viewItemName}
                    hideSelected={hideSelected}
                    onUpdate={onUpdate}
                    isLoading={isLoading}
                    packages={packages}
                    setPackages={setPackages}
                />
            )}
            {((activePanel === DesignPanels.FIELDS &&
                getCurrentForm().view === FormViewEnum.table) ||
                activePanel === DesignPanels.SUBFORMS) && (
                <TableContent
                    data={data}
                    updateData={updateData}
                    activePanel={activePanel}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    resetSelected={resetSelected}
                    foundItemName={foundItemName}
                    viewItemName={viewItemName}
                    setFoundItemName={setFoundItemName}
                    moveItems={moveItems}
                    hideSelected={hideSelected}
                    onUpdate={onUpdate}
                    isLoading={isLoading}
                    packages={packages}
                    setPackages={setPackages}
                />
            )}
            {activePanel === DesignPanels.ACTIONS && (
                <ActionsContent
                    data={data}
                    updateData={updateData}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    resetSelected={resetSelected}
                    foundItemName={foundItemName}
                    viewItemName={viewItemName}
                    setFoundItemName={setFoundItemName}
                    moveItems={moveItems}
                    hideSelected={hideSelected}
                    packages={packages}
                    setPackages={setPackages}
                    isLoading={isLoading}
                />
            )}
            {activePanel === DesignPanels.PANELS && (
                <PanelsContent
                    data={data}
                    onUpdate={onUpdate}
                    isLoading={isLoading}
                    packages={packages}
                />
            )}
        </Flex>
    );
};

const TableContent = ({
    activePanel,
    data,
    selectedItems,
    setSelectedItems,
    resetSelected,
    foundItemName,
    setFoundItemName,
    moveItems,
    viewItemName,
    hideSelected,
    updateData,
    onUpdate,
    isLoading,
    packages,
    setPackages
}) => {
    if (data.length && activePanel === DesignPanels.FIELDS) {
        const coverIndex = data.find(i => i.Cover);
        if (!coverIndex) {
            data[0].Cover = true;
        }
    }
    return (
        <>
            <SearchInput
                placeholder={
                    activePanel === DesignPanels.SUBFORMS ? text.searchSubform : text.searchField
                }
                data={data}
                setFoundItemName={setFoundItemName}
            />
            <Buttons
                selectedCount={selectedItems.length}
                resetSelected={resetSelected}
                moveItems={moveItems}
                hideSelected={hideSelected}
            />
            {isLoading ? (
                <Loader />
            ) : (
                <List
                    data={data}
                    setSelectedItems={setSelectedItems}
                    selectedItems={selectedItems}
                    foundItemName={foundItemName}
                    viewItemName={viewItemName}
                    onUpdate={onUpdate}
                    updateData={updateData}
                    packages={packages}
                    setPackages={setPackages}
                />
            )}
        </>
    );
};

const TabViewContent = ({
    data,
    updateData,
    selectedItems,
    setSelectedItems,
    resetSelected,
    foundItemName,
    setFoundItemName,
    viewItemName,
    setViewItem,
    hideSelected,
    onUpdate,
    pic,
    setPic,
    isLoading,
    packages,
    setPackages
}) => {
    const [activeTab, setActiveTab] = useState(null);

    const moveItems = (dir: MoveDirection) => {
        let result;
        //moving inside a tab
        if (activeTab !== null) {
            const serializedData = serializeTabData(data);
            const reorderedSection = reorderSimpleList(
                dir,
                (serializedData[activeTab] as priority.netitems.form.RowViewTabsDesign)
                    .ColumnsDesign,
                selectedItems
            );
            setViewItem(
                selectedItems.filter(i =>
                    (
                        data[activeTab] as priority.netitems.form.RowViewTabsDesign
                    ).ColumnsDesign.includes(i)
                )
            );
            serializedData[activeTab].ColumnsDesign = reorderedSection;
            result = deserializeTabData(serializedData);
        } else {
            const selected = [];
            data.map(tab => {
                const isSelected = tab.ColumnsDesign.every(i =>
                    selectedItems.find(
                        selected => i.Name === selected.Name || i.Name === selected.merged?.Name
                    )
                );
                isSelected && selected.push(tab);
            });
            result = reorderSimpleList(dir, data, selected);
        }
        updateData(result);
    };

    const moveItemsBetweenTabs = (targetTabIndex: number) => {
        const serializedData = serializeTabData(data);
        serializedData.map((tab, sourceTabIndex) =>
            tab.ColumnsDesign.map(item => {
                if (
                    selectedItems.includes(item) &&
                    !serializedData[targetTabIndex].ColumnsDesign.includes(item)
                ) {
                    moveItemToTab(serializedData, sourceTabIndex, targetTabIndex, item);
                }
            })
        );
        setViewItem(selectedItems);
        updateData(deserializeTabData(serializedData));
    };

    const moveItemToTab = (
        result: priority.netitems.form.RowViewTabsDesign[],
        sourceTabIndex: number,
        targetTabIndex: number,
        item: priority.netitems.form.IRowViewColumnsDesign
    ) => {
        const sourceTabClone: priority.netitems.form.IRowViewColumnsDesign[] = Array.from(
            result[sourceTabIndex].ColumnsDesign
        );
        const deleted = sourceTabClone.splice(sourceTabClone.indexOf(item), 1);
        if (deleted.length) {
            result[targetTabIndex].ColumnsDesign.push(deleted[0]);
            result[sourceTabIndex].ColumnsDesign = sourceTabClone;
        }
    };

    const moveItemBetweenTabs = (
        sourceTabIndex: number,
        targetTabIndex: number,
        item: priority.netitems.form.IRowViewColumnsDesign
    ) => {
        const serializedData = serializeTabData(data);
        moveItemToTab(serializedData, sourceTabIndex, targetTabIndex, item);
        setViewItem([item]);
        updateData(deserializeTabData(serializedData));
    };

    const addTab = () => {
        const newTab = {isNew: true, Title: '', ColumnsDesign: []};
        updateData([...data, newTab]);
    };

    return (
        <>
            <Flex>
                <SearchInput
                    placeholder={text.searchField}
                    data={data.flatMap(tab => tab.ColumnsDesign)}
                    setFoundItemName={setFoundItemName}
                />
                <Button
                    skin="secondary"
                    prefixIcon={PriorityIcon.add}
                    componentClasses={styles.addTabBtn}
                    onClick={addTab}
                >
                    {text.addTab}
                </Button>
            </Flex>
            <Buttons
                selectedCount={selectedItems.length}
                resetSelected={resetSelected}
                moveItems={moveItems}
                data={data}
                moveItemsBetweenTabs={moveItemsBetweenTabs}
                hideSelected={hideSelected}
            />
            {isLoading ? (
                <Loader />
            ) : (
                <TabViewList
                    data={data}
                    pic={pic}
                    setPic={setPic}
                    updateData={updateData}
                    setSelectedItems={setSelectedItems}
                    selectedItems={selectedItems}
                    foundItemName={foundItemName}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    viewItemName={viewItemName}
                    moveItemBetweenTabs={moveItemBetweenTabs}
                    onUpdate={onUpdate}
                    packages={packages}
                    setPackages={setPackages}
                />
            )}
        </>
    );
};

export const ActionsContent = ({
    data,
    selectedItems,
    setSelectedItems,
    resetSelected,
    updateData,
    foundItemName,
    setFoundItemName,
    moveItems,
    viewItemName,
    hideSelected,
    isLoading,
    packages,
    setPackages
}) => {
    return (
        <>
            <SearchInput
                placeholder={text.searchAction}
                data={data}
                setFoundItemName={setFoundItemName}
            />
            <Buttons
                selectedCount={selectedItems.length}
                resetSelected={resetSelected}
                moveItems={moveItems}
                hideSelected={hideSelected}
            />
            {isLoading ? (
                <Loader />
            ) : (
                <ActionsList
                    data={data}
                    updateData={updateData}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    foundItemName={foundItemName}
                    viewItemName={viewItemName}
                    packages={packages}
                    setPackages={setPackages}
                />
            )}
        </>
    );
};

const PanelsContent = ({data, onUpdate, isLoading, packages}) => {
    return (
        <>
            {isLoading ? (
                <Loader />
            ) : (
                <div className={styles.panelList}>
                    {data.map(item => {
                        return (
                            <SimpleItem
                                key={item.PackageId}
                                item={item}
                                onUpdate={onUpdate}
                                menuData={getFieldMenuData(
                                    item,
                                    null,
                                    false,
                                    packages.find(p => p.PackageId === item.PackageId)
                                )}
                                pack={packages.find(p => p.PackageId === item.PackageId)}
                            />
                        );
                    })}
                </div>
            )}
        </>
    );
};
